import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import { Home, Package, ShoppingCart, FileText, Warehouse, Users, ShoppingBag, Upload, Download, LogOut, Barcode, UserCircle, Calculator, Shield, BarChart3, ArrowLeftRight, ClipboardEdit, Bell, PackagePlus, ClipboardCheck, FileQuestion, RotateCcw, FolderOpen, ClipboardList } from 'lucide-react';

const Layout = ({ children, user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [notifCount, setNotifCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [mutedTypes, setMutedTypes] = useState([]);
  const [complianceAlerts, setComplianceAlerts] = useState([]);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const data = await api.getNotificationCount();
        setNotifCount(data.count || 0);
      } catch {}
    };
    const fetchMuted = async () => {
      try {
        const data = await api.request('/api/notifications/muted');
        setMutedTypes(data.muted_types || []);
      } catch {}
    };
    const fetchCompliance = async () => {
      try {
        const data = await api.request('/api/compliance/status');
        setComplianceAlerts(data.notifications || []);
      } catch {}
    };
    fetchCount();
    fetchMuted();
    fetchCompliance();
    const interval = setInterval(() => { fetchCount(); fetchCompliance(); }, 30000);
    return () => clearInterval(interval);
  }, []);

  const toggleNotifs = async () => {
    if (!showNotifs) {
      try {
        const data = await api.getNotifications(true);
        setNotifications(data);
      } catch {}
    }
    setShowNotifs(!showNotifs);
  };

  const markAllRead = async () => {
    try {
      await api.markAllNotificationsRead();
      setNotifCount(0);
      setNotifications([]);
      setShowNotifs(false);
    } catch {}
  };

  const toggleMute = async (type) => {
    try {
      if (mutedTypes.includes(type)) {
        await api.request(`/api/notifications/mute/${type}`, { method: 'DELETE' });
        setMutedTypes(prev => prev.filter(t => t !== type));
      } else {
        await api.request('/api/notifications/mute', { method: 'POST', body: JSON.stringify({ type }) });
        setMutedTypes(prev => [...prev, type]);
      }
    } catch {}
  };

  const navGroups = [
    { items: [{ name: 'Dashboard', href: '/dashboard', icon: Home }] },
    { heading: 'Inventory Management', items: [
      { name: 'Products', href: '/products', icon: Package },
      { name: 'Product Categories', href: '/product-categories', icon: FolderOpen },
      { name: 'Stock Movements', href: '/stock-movements', icon: ArrowLeftRight },
      { name: 'Stock Adjustments', href: '/stock-adjustments', icon: ClipboardEdit },
      { name: 'Stock Count', href: '/stock-count', icon: ClipboardCheck },
      { name: 'Inventory', href: '/inventory', icon: Warehouse },
    ]},
    { heading: 'Sales Orders', items: [
      { name: 'Sales Orders', href: '/sales-orders', icon: ShoppingCart },
      { name: 'Sales Order Quotes', href: '/sales-quotes', icon: FileQuestion },
      { name: 'Sales Order Returns', href: '/sales-returns', icon: RotateCcw },
      { name: 'POS', href: '/pos', icon: ShoppingBag },
      { name: 'Customers', href: '/customers', icon: UserCircle },
      { name: 'Account Statements', href: '/account-statements', icon: FileText },
      { name: 'Payments Received', href: '/payments-received', icon: Calculator },
    ]},
    { heading: 'Adding New Stock', items: [
      { name: 'Purchase Orders', href: '/purchase-orders', icon: FileText },
      { name: 'Purchase Order Quotes', href: '/purchase-quotes', icon: FileQuestion },
      { name: 'Shipments Received', href: '/shipments-received', icon: PackagePlus },
      { name: 'Shipments Returned', href: '/shipment-returns', icon: RotateCcw },
      { name: 'Vendors', href: '/vendors', icon: Users },
      { name: 'Import', href: '/import', icon: Upload, adminOnly: true },
      { name: 'Export', href: '/export', icon: Download, adminOnly: true },
    ]},
    { heading: 'Reports', items: [
      { name: 'Reports', href: '/reports', icon: BarChart3 },
      { name: 'Sales Tax Report', href: '/reports/sales-tax', icon: Calculator },
      { name: 'Inventory Reports', href: '/inventory-reports', icon: ClipboardList },
      { name: 'Barcode Generator', href: '/barcode-generator', icon: Barcode },
    ]},
    { heading: 'Admin', adminOnly: true, items: [
      { name: 'Users', href: '/admin/users', icon: Users, adminOnly: true },
      { name: 'Pricing Schemes', href: '/admin/pricing-schemes', icon: Calculator, adminOnly: true },
      { name: 'Login History', href: '/admin/login-history', icon: Shield, adminOnly: true },
    ]},
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo.jpg" alt="Webb Supply" className="h-9 w-9 rounded-md object-cover" />
              <div>
                <h1 className="text-xl font-semibold tracking-tight leading-tight text-gray-900">Webb Supply</h1>
                <p className="text-[10px] text-gray-500 leading-tight">A Division of Patrick Webb Oil, LLC</p>
              </div>
            </div>
            {user && (
              <div className="flex items-center gap-4">
                <div className="relative">
                  <button onClick={toggleNotifs} className="p-2 hover:bg-gray-100 rounded-md transition-colors relative" data-testid="notif-bell">
                    <Bell className="h-5 w-5 text-gray-500" />
                    {notifCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">{notifCount > 9 ? '9+' : notifCount}</span>
                    )}
                  </button>
                  {showNotifs && (
                    <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-lg shadow-xl z-50" data-testid="notif-dropdown">
                      <div className="flex items-center justify-between px-4 py-3 border-b">
                        <span className="text-sm font-semibold">Notifications</span>
                        {notifications.length > 0 && (
                          <button onClick={markAllRead} className="text-xs text-primary hover:underline" data-testid="mark-all-read">Mark all read</button>
                        )}
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <p className="px-4 py-6 text-sm text-muted-foreground text-center">No new notifications</p>
                        ) : notifications.map(n => (
                          <div key={n.id} className="px-4 py-3 border-b border-border/50 hover:bg-muted/20 text-sm">
                            <div className="flex items-start gap-2">
                              {(n.type === 'out_of_stock') && <span className="mt-0.5 flex-shrink-0 h-2 w-2 rounded-full bg-red-500" />}
                              {(n.type === 'low_stock') && <span className="mt-0.5 flex-shrink-0 h-2 w-2 rounded-full bg-amber-500" />}
                              {(n.type !== 'out_of_stock' && n.type !== 'low_stock') && <span className="mt-0.5 flex-shrink-0 h-2 w-2 rounded-full bg-blue-500" />}
                              <p className="flex-1">{n.message}</p>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 ml-4">{new Date(n.created_at).toLocaleString()}</p>
                            {(n.type === 'low_stock' || n.type === 'out_of_stock') && n.variant_id && (
                              <label className="flex items-center gap-1.5 mt-1.5 ml-4 cursor-pointer text-xs text-muted-foreground hover:text-foreground"
                                data-testid={`mute-${n.type}-${n.variant_id}`}>
                                <input type="checkbox"
                                  checked={mutedTypes.includes(`${n.type}:${n.variant_id}`)}
                                  onChange={() => toggleMute(`${n.type}:${n.variant_id}`)}
                                  className="rounded border-border h-3 w-3" />
                                Mute this product's {n.type === 'out_of_stock' ? 'out-of-stock' : 'low stock'} alerts
                              </label>
                            )}
                            {n.type === 'price_change' && (
                              <label className="flex items-center gap-1.5 mt-1.5 ml-4 cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                                <input type="checkbox" checked={mutedTypes.includes('price_change')} onChange={() => toggleMute('price_change')}
                                  className="rounded border-border h-3 w-3" data-testid="mute-price-change" />
                                Mute price change alerts
                              </label>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{user.full_name}</p>
                  <p className="text-xs text-gray-500 capitalize">{user.role}</p>
                </div>
                <button
                  onClick={onLogout}
                  className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                  title="Logout"
                  aria-label="Log out"
                  data-testid="logout-btn"
                >
                  <LogOut className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-[#1a2744] min-h-[calc(100vh-4rem)] overflow-y-auto">
          <nav className="p-4 space-y-1">
            {navGroups.map((group, gi) => {
              const visibleItems = group.items.filter(item => !item.adminOnly || user?.role === 'admin');
              if (visibleItems.length === 0) return null;
              return (
                <div key={gi}>
                  {gi > 0 && <div className="my-3 bg-white h-2 -mx-4" />}
                  {group.heading && (
                    <p className="px-3 pt-1 pb-2 text-xs font-semibold text-blue-300 uppercase tracking-wider">{group.heading}</p>
                  )}
                  {visibleItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          isActive
                            ? 'bg-primary text-white'
                            : 'text-[#1a2744] bg-white hover:bg-gray-100 hover:text-[#1a2744]'
                        }`}
                      >
                        <Icon className="mr-3 h-5 w-5" />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              );
            })}
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 p-6">
          {complianceAlerts.length > 0 && (
            <div className="mb-4 space-y-2" data-testid="compliance-alerts">
              {complianceAlerts.map((alert, i) => (
                <div key={i}
                  onClick={() => { if (alert.link) navigate(alert.link); }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium ${
                  alert.link ? 'cursor-pointer hover:opacity-80' : ''
                } ${
                  alert.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
                  alert.type === 'warning' ? 'bg-amber-50 text-amber-800 border border-amber-200' :
                  'bg-blue-50 text-blue-800 border border-blue-200'
                }`} data-testid={`compliance-alert-${alert.category}`}>
                  <Bell className="h-4 w-4 flex-shrink-0" />
                  <span className="flex-1">{alert.message}</span>
                  {alert.link && <span className="text-xs opacity-60">Click to view &rarr;</span>}
                  {alert.category === 'inventory_count' && user?.role === 'admin' && (
                    <button onClick={async (e) => {
                      e.stopPropagation();
                      try { await api.request('/api/compliance/override-inventory-count', { method: 'POST' }); setComplianceAlerts(prev => prev.filter(a => a.category !== 'inventory_count')); } catch {}
                    }} className="ml-auto text-xs px-2 py-1 bg-white border rounded hover:bg-gray-50" data-testid="override-inventory-btn">
                      Admin Override
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
